// External dependencies
import React, { useEffect, useState } from "react"
import swal from "sweetalert"
import SVG from "react-inlinesvg"
import { Button, Card } from "react-bootstrap"
import { useLocation } from "react-router"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

// Internal dependencies
import { ListPaid } from "./ListPaid.jsx"
import { ListUnpaid } from "./ListUnpaid.jsx"
import { ListPartial } from "./ListPartial.jsx"
import { CardBody } from "../../../../../_metronic/_partials/controls/index.js"
import { NoteView, TabsWithIcon } from "../../../../../component/index.jsx"
import { ICON_CONSTANTS, ILLUSTRATION_CONSTANTS } from "../../../../../constants/ImageConstants.js"
import { PRIVILEGE_ENUM } from "../../../../../constants/PrivilegeConstants.js"
import { RoutesConstants } from "../../../../../constants/RoutesConstants.js"
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers/index.js"
import { FloatingSelectArea } from "../../../../../component/atoms/FloatingSelectArea/index.jsx"
import { loadUserLogin } from "../../../../service/userManagementAction.js"
import { ROUTING_STATUS, UNIT_STRING } from "../../../../../constants/StringConstant.js"
import { getUrlParsingPageFilter } from "../../../../../config/EndpointCollection.js"
import { PopupQuestion } from "../../../../../component/atoms/Popup/index.jsx"
import { bulkConfirmationPlantingSeason } from "../../../../../redux/actions/AreaAction.jsx"
import { AREA } from "../../../../../constants/InitTypeConstants.js"
import { countListTotal } from "../../../../helpers/Utils.js"
import { convertM2ToHectareParseFloat, stringFormatting, toRupiah } from "../../../../helpers/TextHelper.js"

export const Table = ({
  data,
  changePage,
  history,
  general,
  isFilter,
  fetchData,
  tab,
  urlParams
}) => {
  const { t } = useTranslation()
  const privileges = loadUserLogin().privilege
  const dispatch = useDispatch()
  const location = useLocation()
  const { General, Area } = useSelector((state) => state)

  const [openConfirmation, setOpenConfirmation] = useState(false)
  const [openConfirmationWithBill, setOpenConfirmationWithBill] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])

  const [tabActive, setTabActive] = useState(
    tab === ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PARTIALLY_PAID
      ? 1
      : tab === ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PAID
      ? 2
      : 0
  )

  const mergeQueryWithTab = (tabValue) => {
    const current = Object.fromEntries(new URLSearchParams(location.search))
    // reset sort on tab change
    const rest = Object.fromEntries(Object.entries(current).filter(([key]) => key !== "sort"))
    const merged = { ...rest, ...urlParams, tab: tabValue }
    return getUrlParsingPageFilter(merged)
  }

  const textInfo = (
    <div className={"d-flex align-items-center text-center"}>
      <SVG className="mr-2" src={ICON_CONSTANTS.NOTE_VIEW_WARNING_TRIANGEL} />
      <div className={`text-warning`}>{t("messages_alert_max_select_area")}</div>
    </div>
  )

  const totalComponent = (
    <div className={`color-white text-capitalize txt-16 pl-4`}>
      {selectedRows.length} {t("land_selected")}{" "}
      <div
        className={"pt-1 text-lowercase font-weight-light d-flex align-items-center txt-14"}
        style={{ gap: 12 }}>
        <span>
          {stringFormatting(
            convertM2ToHectareParseFloat(countListTotal(selectedRows, `land_area`)).toString(),
            UNIT_STRING.HA
          )}
        </span>
        <div className={"separator bg-neutral-gray separator-dot"} />
        <span className={"text-capitalize"}>
          {toRupiah(
            countListTotal(
              selectedRows,
              tab === ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PARTIALLY_PAID
                ? `remaining_bill`
                : `total_bill`
            )
          )}
        </span>
      </div>
    </div>
  )

  const handleBulkConfirmationPlantingSeason = () => {
    if (selectedRows.length) {
      const body = {
        planting_season_ids: selectedRows.map((item) => item?.planting_season_id)
      }
      dispatch(bulkConfirmationPlantingSeason(body))
    }
  }

  const handleBulkConfirmationPlantingSeasonWithBill = () => {
    if (selectedRows.length) {
      history.push(
        RoutesConstants.AREA.SEND_PLANTING_SESSION_REPORT(
          selectedRows.map((e) => e.area_id).join(",")
        ) + `?tab=${tab}`
      )
    }
  }

  const getTabsContents = () => {
    const labelList = []
    const ComponentsList = []

    const unpaid = (
      <ListUnpaid
        data={data}
        changePage={changePage}
        history={history}
        general={general}
        isFilter={isFilter}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    )
    const partial = (
      <ListPartial
        data={data}
        changePage={changePage}
        history={history}
        general={general}
        isFilter={isFilter}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    )
    const paid = (
      <ListPaid
        data={data}
        changePage={changePage}
        history={history}
        general={general}
        isFilter={isFilter}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    )

    if (privileges.indexOf(PRIVILEGE_ENUM.LIST_AREA_ACTIVITY) >= 0) {
      ComponentsList.push(unpaid)
      labelList.push({
        label: t("label_planting_season_unpaid"),
        labelTooltip: "",
        icon: ""
      })
    }
    if (privileges.indexOf(PRIVILEGE_ENUM.LIST_AREA_ACTIVITY) >= 0) {
      ComponentsList.push(partial)
      labelList.push({
        label: t("label_planting_season_partial"),
        labelTooltip: "",
        icon: ""
      })
    }
    if (privileges.indexOf(PRIVILEGE_ENUM.LIST_AREA_ACTIVITY) >= 0) {
      ComponentsList.push(paid)
      labelList.push({
        label: t("label_planting_season_paid"),
        labelTooltip: "",
        icon: ""
      })
    }

    return {
      labels: labelList,
      contents: ComponentsList
    }
  }

  const changeTabs = (data) => {
    setTabActive(data)
    if (data === 1) {
      history.push(
        RoutesConstants.AREA.LIST_AREA_READY_PLANTING_SEASON_CONFIRMATION(
          ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PARTIALLY_PAID
        ) + mergeQueryWithTab(ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PARTIALLY_PAID)
      )
    } else if (data === 2) {
      history.push(
        RoutesConstants.AREA.LIST_AREA_READY_PLANTING_SEASON_CONFIRMATION(
          ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PAID
        ) + mergeQueryWithTab(ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PAID)
      )
    } else if (data === 0) {
      history.push(
        RoutesConstants.AREA.LIST_AREA_READY_PLANTING_SEASON_CONFIRMATION(
          ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.NOT_PAID
        ) + mergeQueryWithTab(ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.NOT_PAID)
      )
    }
  }

  useEffect(() => {
    setTabActive(
      tab === ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PARTIALLY_PAID
        ? 1
        : tab === ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PAID
        ? 2
        : 0
    )
  }, [tab])

  useEffect(() => {
    if (
      !General.loading &&
      General.actionInitType === AREA.BULK_CONFIRMATION_PLANTING_SEASON &&
      Area.responsePost
    ) {
      swal({
        text: t("messages_response_success_bulk_confirm_planting_season"),
        icon: toAbsoluteUrl(ILLUSTRATION_CONSTANTS.SUCCESS_PLANTING_SEASON_CONFIRMATION)
      }).then(() => {
        const result = location?.search
        fetchData(result)
      })
    }
  }, [General, Area.responsePost])

  const messagePopupConfirmation = (
    <div className={"text-center"}>
      <div>{t("messages_confirmation_bulk_planting_season_confirmation")}</div>
      <div>
        <b> {t("messages_confirmation_bulk_planting_season_confirmation_validation")}</b>
      </div>
    </div>
  )

  const messagePopupConfirmationWithBill = (
    <div className={"text-center"}>
      <p>
        Konfirmasi Musim Tanam yang memiliki tagihan mengharuskan
        <br /> Upload <strong>Berita Acara</strong> sebagai langkah selanjutnya.
        <br />
        <br />
        Informasi aktivitas akan dipindahkan ke tab <strong>Riwayat Aktivitas</strong>.
      </p>
      <div
        className="w-100 d-flex pt-12"
        style={{ gap: 12 }}
        onClick={() => setOpenConfirmationWithBill(false)}>
        <Button variant="secondary-info" color="muted" className="w-100">
          {t("button_cancel")}
        </Button>
        <Button
          className="w-100"
          onClick={() => {
            setOpenConfirmationWithBill(false)
            setSelectedRows([])
            handleBulkConfirmationPlantingSeasonWithBill()
          }}>
          {t("button_next")}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      {/* Popup for approve paid */}
      <PopupQuestion
        show={openConfirmation}
        title={t("messages_title_bulk_planting_season_confirmation")}
        body={messagePopupConfirmation}
        textOk={t("button_continue")}
        variantOkButton={"primary"}
        onCancel={() => {
          setOpenConfirmation(false)
        }}
        onOk={() => {
          setOpenConfirmation(false)
          setSelectedRows([])
          handleBulkConfirmationPlantingSeason()
        }}
        textCancel={t("button_cancel")}
      />

      {/* Popup for approve with bill */}
      <PopupQuestion
        show={openConfirmationWithBill}
        title={t("messages_title_planting_season_confirmation")}
        body={messagePopupConfirmationWithBill}
        isUseFooter={false}
      />

      {selectedRows.length > 1 && (
        <FloatingSelectArea
          buttonlabel={t("label_confirmation_planting_season_bulk")}
          withIcon={true}
          labelIcon={ICON_CONSTANTS.LAND_AREA_WHITE}
          handleClick={() => {
            tab === ROUTING_STATUS.PLANTING_SEASON_STATUS_STRING.PAID
              ? setOpenConfirmation(true)
              : setOpenConfirmationWithBill(true)
          }}
          buttonColor={"primary"}
          disableButton={selectedRows.length > 20 ? true : false}
          showInfo={selectedRows.length > 20 ? true : false}
          infoText={textInfo}
          totalComponent={totalComponent}
        />
      )}
      <Card>
        <CardBody>
          <NoteView
            variant={"success"}
            title={
              <>
                <div className="pl-6">{t("label_planting_season_list_note")}</div>
              </>
            }
          />

          <TabsWithIcon
            labelTab={getTabsContents().labels}
            componentTab={getTabsContents().contents}
            changeTab={changeTabs}
            initValue={tabActive}
            wrapperClass="px-0"
          />
        </CardBody>
      </Card>
    </>
  )
}
